export interface Business {
    id: string;
    name: string;
}

export interface Team {
    id: string;
    name: string;
    business: string;
}

export interface PermissionOption {
    value: string; // Format: "businessId.teamId.service" or "All Business.All Team.service"
    label: string; // Format: "Business Name⸬Team Name⸬Service Name"
    business: string;
    product: string;
    service: string;
}

// Generate permission options from business and team lists
export function generatePermissionOptions(
    services: readonly { value: string; label: string }[],
    businessList: Business[],
    teamList: Team[]
): PermissionOption[] {
    const options: PermissionOption[] = [];
    const serviceValues = services.map((s) => s.value);

    // 1. Add global "All Business - All Team" option for each service
    serviceValues.forEach((service) => {
        options.push({
            value: "*.*.*.r", // Will be adjusted based on selected level
            label: `All Business⸬All Team⸬${service}`,
            business: "*",
            product: "*",
            service,
        });
    });

    // 2. Add "Business - All Team" combinations for each business
    businessList.forEach((business) => {
        serviceValues.forEach((service) => {
            options.push({
                value: `${business.id}.*.${service}.r`, // Will be adjusted based on selected level
                label: `${business.id}⸬All Team⸬${service}`,
                business: business.id,
                product: "*",
                service,
            });
        });
    });

    // 3. Add specific "Business - Team - Service" combinations
    businessList.forEach((business) => {
        const teamsForBusiness = teamList.filter((team) => team.business === business.id);

        teamsForBusiness.forEach((team) => {
            serviceValues.forEach((service) => {
                options.push({
                    value: `${business.id}.${team.id}.${service}.r`, // Will be adjusted based on selected level
                    label: `${business.name}⸬${team.name}⸬${service}`,
                    business: business.id,
                    product: team.id,
                    service,
                });
            });
        });
    });

    return options;
}

export function getBasePermission(permission: string): string {
    const parts = permission.split(".");
    if (parts.length >= 4) {
        return parts.slice(0, -1).join(".");
    }
    return permission;
}

export function createBusinessNameMapping(businessList: Business[]): Record<string, string> {
    return businessList.reduce((acc, business) => {
        acc[business.id] = business.name;
        return acc;
    }, {} as Record<string, string>);
}

export function createTeamNameMapping(teamList: Team[]): Record<string, string> {
    return teamList.reduce((acc, team) => {
        acc[team.id] = team.name;
        return acc;
    }, {} as Record<string, string>);
}
